import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import type { FeatureFlagClient, FeatureFlagConfig } from './types';

export interface CreateFeatureFlagsOptions<TFlags extends Record<string, string>> {
  /** The app's flag registry. Its values are the Unleash flag names. */
  flags: TFlags;
  /** Resolves client configuration, or `null` when flags are not configured. */
  loadConfig: () => FeatureFlagConfig | null;
  /** Builds a client from a resolved configuration. */
  createClient: (config: FeatureFlagConfig) => FeatureFlagClient;
  /**
   * Flags to force to a fixed value, keyed by flag name. Loosely typed because
   * this normally comes from an environment variable: unknown names are
   * dropped, not trusted.
   */
  overrides?: Readonly<Record<string, boolean>>;
}

export interface FeatureFlagProviderProps<TName extends string> {
  children: ReactNode;
  /**
   * Forces flags to fixed values for everything below this provider. Intended
   * for tests; production code should let Unleash decide.
   */
  overrides?: Partial<Record<TName, boolean>>;
}

export interface FeatureFlagGateProps<TName extends string> {
  /** Flag that controls whether `children` are rendered. */
  flag: TName;
  /** Rendered when the flag is disabled. Defaults to nothing. */
  fallback?: ReactNode;
  children: ReactNode;
}

const shallowEqual = (a: Record<string, boolean>, b: Record<string, boolean>): boolean => {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => a[key] === b[key]);
};

/**
 * Builds a typed feature-flag binding around a client.
 *
 * Created here but started by `identify`, so the first request already carries a
 * `userId`. Starting anonymously first would paint the flag-off branch and swap
 * it a round trip later.
 *
 * When `loadConfig()` returns `null` no client is built at all: every flag
 * resolves to its override or `false`, and `ready` is `true` so consumers never
 * block on a client that will never arrive. That is the default state in local
 * development, unit tests and Playwright, where the Unleash credentials are
 * blank.
 */
export function createFeatureFlags<const TFlags extends Record<string, string>>(
  options: CreateFeatureFlagsOptions<TFlags>,
) {
  type FlagName = TFlags[keyof TFlags];

  const names = Object.values(options.flags) as FlagName[];
  const knownNames = new Set<string>(names);

  /** Drops override keys that are not in the registry, loudly. */
  const sanitiseOverrides = (
    raw: Readonly<Record<string, boolean | undefined>> | undefined,
  ): Partial<Record<FlagName, boolean>> => {
    const sanitised: Partial<Record<FlagName, boolean>> = {};
    if (!raw) {
      return sanitised;
    }
    Object.entries(raw).forEach(([name, value]) => {
      if (value === undefined) {
        return;
      }
      if (knownNames.has(name)) {
        sanitised[name as FlagName] = value;
      } else {
        console.warn(`Feature flags: ignoring override for unknown flag "${name}".`);
      }
    });
    return sanitised;
  };

  const baseOverrides = sanitiseOverrides(options.overrides);

  const config = options.loadConfig();
  const client = config ? options.createClient(config) : null;

  /** Long enough that a real /me beats it; short enough to bound the spinner. */
  const IDENTIFY_DEADLINE_MS = 3000;

  let identity: string | undefined;
  let started = false;
  let deadline: ReturnType<typeof setTimeout> | undefined;

  /**
   * `started` is load-bearing: the SDK guards re-entry on a `timerRef` assigned
   * two awaits into `start()`, so same-tick calls each install an interval.
   */
  const startOnce = (): void => {
    if (!client || started) {
      return;
    }
    started = true;
    void client.start();
  };

  /** `updateContext` before `start()` only sets the context, so no request is wasted. */
  const identify = (userId: string): void => {
    if (!client || userId === identity) {
      return;
    }
    identity = userId;
    // Cleared before the await, so the deadline cannot start behind us. `.catch`
    // because once it is cleared, a rejection would leave nothing to start.
    clearTimeout(deadline);
    void client.updateContext({ userId }).then(startOnce).catch(startOnce);
  };

  // Nothing identified: start anyway, or `ready` never latches and every
  // ready-gated consumer hangs.
  if (client) {
    deadline = setTimeout(startOnce, IDENTIFY_DEADLINE_MS);
  }

  /** Overrides always win, whether or not a client is configured. */
  const evaluate = (
    localOverrides: Partial<Record<FlagName, boolean>>,
  ): Record<FlagName, boolean> => {
    const values = {} as Record<FlagName, boolean>;
    names.forEach((name) => {
      const override = localOverrides[name] ?? baseOverrides[name];
      values[name] = override ?? (client ? client.isEnabled(name) : false);
    });
    return values;
  };

  interface FeatureFlagState {
    values: Record<FlagName, boolean>;
    ready: boolean;
  }

  const buildState = (localOverrides: Partial<Record<FlagName, boolean>>): FeatureFlagState => ({
    values: evaluate(localOverrides),
    ready: client ? client.isReady() : true,
  });

  // The no-provider fallback, which `app.tsx` always displaces. Deliberately
  // not built with `buildState`: that would call isEnabled() for every flag at
  // module-eval time — before any data has landed — and each call is counted as
  // an impression in Unleash's metrics.
  const FeatureFlagContext = createContext<FeatureFlagState>({
    values: {} as Record<FlagName, boolean>,
    ready: true,
  });

  const FeatureFlagProvider: FC<FeatureFlagProviderProps<FlagName>> = ({ children, overrides }) => {
    const localOverrides = useMemo(() => sanitiseOverrides(overrides), [overrides]);
    const [state, setState] = useState<FeatureFlagState>(() => buildState(localOverrides));

    useEffect(() => {
      const sync = () => {
        setState((previous) => {
          const next = buildState(localOverrides);
          // Keep the previous identity when nothing changed, so the 15-second
          // poll ticks that resolve to the same values do not re-render.
          if (previous.ready === next.ready && shallowEqual(previous.values, next.values)) {
            return previous;
          }
          return next;
        });
      };

      // Runs before the client guard: with no client this is the only thing
      // that picks up a changed `overrides` prop after mount, which is the
      // path local development, Playwright and component tests all take.
      sync();

      if (!client) {
        return;
      }

      client.on('ready', sync);
      client.on('update', sync);

      return () => {
        client.off('ready', sync);
        client.off('update', sync);
      };
    }, [localOverrides]);

    return <FeatureFlagContext.Provider value={state}>{children}</FeatureFlagContext.Provider>;
  };

  /**
   * Reads a single flag. Resolves to `false` when flags are not configured, and
   * when no provider is mounted above the caller.
   */
  const useFeatureFlag = (flag: FlagName): boolean =>
    useContext(FeatureFlagContext).values[flag] ?? false;

  /**
   * Whether the first set of flags has loaded, reflecting the current user
   * unless identification took longer than the deadline. Always `true` when
   * flags are not configured. Gate route-level switches on this to avoid
   * painting the flag-off branch before the first fetch lands.
   */
  const useFeatureFlagsReady = (): boolean => useContext(FeatureFlagContext).ready;

  const FeatureFlagGate: FC<FeatureFlagGateProps<FlagName>> = ({
    flag,
    fallback = null,
    children,
  }) => {
    const enabled = useFeatureFlag(flag);
    return <>{enabled ? children : fallback}</>;
  };

  return {
    FeatureFlagProvider,
    FeatureFlagGate,
    useFeatureFlag,
    useFeatureFlagsReady,
    identify,
    /**
     * Stops the client on HMR, and the deadline with it: otherwise it fires
     * after `stop()` and leaves an interval on a dead client.
     */
    dispose: () => {
      clearTimeout(deadline);
      client?.stop();
    },
  };
}
